import React, { useState, useEffect } from 'react';
import { useEmbedded } from '../../hooks/useEmbedded';
import { 
  Container, 
  Typography, 
  Box, 
  Grid, 
  Card, 
  CardContent, 
  Button,
  ToggleButtonGroup,
  ToggleButton,
  LinearProgress,
  Chip,
  CircularProgress
} from '@mui/material';
import { ViewList, ViewModule, School, AutoAwesomeMotion, CalendarMonth } from '@mui/icons-material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import moment from 'moment';
import '../Course/CourseGrid.css'; // Reuse the same CSS

const AllLPathInstances = () => {
  const [viewMode, setViewMode] = useState('grid');
  const [loading, setLoading] = useState(true);
  const [lpathData, setLpathData] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { id } = useParams(); // Get learning path ID from URL params
  const location = useLocation();
  const isEmbedded = useEmbedded();
  const stateData = location.state;

  useEffect(() => {
    // If we have state data, use it directly
    if (stateData && stateData.instances) {
      setLpathData(stateData);
      setLoading(false);
      return;
    }

    // Otherwise, show error - we expect data to be passed via state
    setError('Instance data not available');
    setLoading(false);
  }, [stateData]);

  const handleViewChange = (event, newView) => {
    if (newView !== null) {
      setViewMode(newView);
    }
  };

  const handleSelectInstance = (instanceId) => {
    const pathPrefix = isEmbedded ? '/embed' : '';
    navigate(`${pathPrefix}/learner/lpath/${id}/${instanceId}`);
  };

  const formatDate = (dateString) => {
    return dateString ? moment(dateString).format('MMM DD, YYYY') : 'Not specified';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !lpathData) {
    return (
      <Container>
        <Typography variant="h6" color="error" align="center">
          {error || 'Learning path instances not found'}
        </Typography>
      </Container>
    );
  }

  // Extract the necessary data from lpathData
  const instances = lpathData?.instances || [];
  const enrolledInstanceId = lpathData?.enrolledInstanceId;
  const progress = lpathData?.progress;
  const lpathId = lpathData?.lpathId || id;
  const lpathTitle = lpathData?.lpath?.title || 'Learning Path';

  // Handle the case when instances is undefined or empty
  if (!instances || instances.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column', height: '50vh' }}>
          <Typography variant="h5" gutterBottom>No instances available</Typography>
          <Button 
            variant="contained" 
            onClick={() => navigate(`/learner/lpath/${lpathId}`)}
            sx={{ mt: 2 }}
          >
            Back to Learning Path
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">{lpathTitle} - Instances</Typography>
        <Box>
          <Button 
            variant="outlined" 
            onClick={() => {
              // Add embedded path prefix if in embedded mode
              const pathPrefix = isEmbedded ? '/embed' : '';
              navigate(`${pathPrefix}/learner/lpath/${lpathId}`);
            }}
            sx={{ mr: 2 }}
          >
            Back to Learning Path
          </Button>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewChange}
            aria-label="view mode"
          >
            <ToggleButton value="list" aria-label="list view">
              <ViewList />
            </ToggleButton>
            <ToggleButton value="grid" aria-label="grid view">
              <ViewModule />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      {viewMode === 'grid' ? (
        <Grid container spacing={3}>
          {instances.map((instance) => (
            <Grid item xs={12} sm={6} md={4} key={instance.id}>
              <div className="course-card-wrapper">
                <div className="course-card">
                  <div className="hover-overlay">          
                    <Box className="course-description">
                      <Typography 
                        component="div"
                        className="card-description"
                      >
                        {instance.description || `${instance.completion_deadline ? `Complete by ${formatDate(instance.completion_deadline)}.` : ''}`}
                      </Typography>
                    </Box>
                  </div>
                  
                  {/* Icon instead of image */}
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    height: '100%', 
                    backgroundColor: '#f5f5f5'
                  }}>
                    <AutoAwesomeMotion style={{ 
                      fontSize: 80, 
                      color: enrolledInstanceId === instance.id ? '#4caf50' : '#2563eb' 
                    }} />
                  </div>
                  
                  {/* Progress Bar at Bottom (if enrolled) */}
                  {enrolledInstanceId === instance.id && progress && (
                    <div style={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      width: '100%',
                      height: '4px',
                      backgroundColor: '#e0e0e0',
                      borderBottomLeftRadius: '8px',
                      borderBottomRightRadius: '8px',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        width: `${progress.percentage || progress.progress || 0}%`,
                        height: '100%',
                        backgroundColor: '#2563eb'
                      }}></div>
                    </div>
                  )}
                </div>
                
                <div className="card-footer">
                  <Box sx={{ mb: 2 }}>
                    <h3 className="card-title">
                      {instance.title || `Instance ${instance.id}`}
                    </h3>
                    <Typography variant="body2" color="textSecondary" sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      {instance.default_instance === '1' && (
                        <Chip 
                          label="Default" 
                          size="small" 
                          color="primary" 
                          variant="outlined"
                          sx={{ mr: 1 }}
                        />
                      )}
                    </Typography>
                    
                    {instance.completion_deadline && (
                      <Typography variant="body2" color="textSecondary" sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                        <CalendarMonth fontSize="small" sx={{ mr: 0.5 }} />
                        Complete by: {formatDate(instance.completion_deadline)}
                      </Typography>
                    )}
                  </Box>
                  
                  <Box sx={{ mt: 2, mb: 5, textAlign: 'right' }}>
                    {enrolledInstanceId === instance.id ? (
                      <Button
                        variant="contained"
                        style={{ backgroundColor: '#2563eb' }}
                        onClick={() => handleSelectInstance(instance.id)}
                        sx={{ px: 3, py: 1 }}
                      >
                        Current
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        style={{ backgroundColor: '#2563eb' }}
                        onClick={() => handleSelectInstance(instance.id)}
                        sx={{ px: 3, py: 1 }}
                      >
                        Select
                      </Button>
                    )}
                  </Box>
                </div>
              </div>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Box>
          {instances.map((instance) => (
            <Card 
              key={instance.id} 
              sx={{ 
                mb: 2, 
                border: enrolledInstanceId === instance.id ? '2px solid #4caf50' : 'none'
              }}
            >
              <CardContent>
                <Grid container alignItems="center">
                  <Grid item xs={12} md={1} sx={{ display: 'flex', justifyContent: 'center' }}>
                    <School style={{ 
                      fontSize: 40, 
                      color: enrolledInstanceId === instance.id ? '#4caf50' : '#2563eb' 
                    }} />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="h6">
                      {instance.title || `Instance ${instance.id}`}
                      {instance.default_instance === '1' && (
                        <Chip 
                          label="Default" 
                          size="small" 
                          color="primary" 
                          variant="outlined"
                          sx={{ ml: 1 }}
                        />
                      )}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    {instance.completion_deadline && (
                      <Typography variant="body2" color="textSecondary" sx={{ display: 'flex', alignItems: 'center' }}>
                        <CalendarMonth fontSize="small" sx={{ mr: 0.5 }} />
                        Complete by: {formatDate(instance.completion_deadline)}
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} md={3}>
                    {enrolledInstanceId === instance.id && progress && (
                      <Box sx={{ width: '100%' }}>
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
                          Progress: {Math.round(progress.percentage || progress.progress || 0)}%
                        </Typography>
                        <LinearProgress 
                          variant="determinate" 
                          value={progress.percentage || progress.progress || 0}
                          sx={{ 
                            height: 8, 
                            borderRadius: 4,
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: '#2563eb'
                            }
                          }}
                        />
                      </Box>
                    )}
                  </Grid>
                  <Grid item xs={12} md={2} sx={{ textAlign: 'right' }}>
                    <Button 
                      variant="contained" 
                      style={{ backgroundColor: '#2563eb' }}
                      onClick={() => handleSelectInstance(instance.id)}
                      size="small"
                    >
                      {enrolledInstanceId === instance.id ? "Current" : "Select"}
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}
    </Container>
  );
};

export default AllLPathInstances;